import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../lib/auth/AuthProvider';
import { downloadAuditCsv, getAuditLog, type AuditEvent } from '../lib/advisorApi';
import { formatDateTime, humanize } from '../lib/format';
import './advisor.css';

const PAGE_SIZE = 50;

export function AuditLogScreen() {
  const { api, accessToken } = useAuth();
  const [rows, setRows] = useState<AuditEvent[] | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [eventType, setEventType] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await getAuditLog(api, {
        limit: PAGE_SIZE,
        offset,
        eventType: eventType.trim() === '' ? undefined : eventType.trim(),
      });
      setRows(res.rows);
      setTotal(res.total);
    } catch {
      setError('We could not load the audit log.');
    }
  }, [api, offset, eventType]);

  useEffect(() => {
    void load();
  }, [load]);

  async function exportCsv(): Promise<void> {
    const token = accessToken();
    if (token === null) return;
    setBusy(true);
    setError(null);
    try {
      await downloadAuditCsv(token, {
        eventType: eventType.trim() === '' ? undefined : eventType.trim(),
      });
    } catch {
      setError('The export could not be generated.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="la-rise">
      <header className="page__head">
        <p className="la-label">Compliance</p>
        <h1 className="page__title">Audit log</h1>
        <p className="page__sub">
          Every action taken on a vault in your book, in order, with who did it and when. Export it
          for your compliance file.
        </p>
      </header>

      <div className="toolbar">
        <input
          className="field"
          type="search"
          placeholder="Filter by event type"
          aria-label="Filter by event type"
          value={eventType}
          onChange={(e) => {
            setOffset(0);
            setEventType(e.target.value);
          }}
        />
        <button
          type="button"
          className="la-btn-secondary"
          disabled={busy}
          onClick={() => void exportCsv()}
        >
          {busy ? 'Preparing' : 'Export CSV'}
        </button>
      </div>

      {error !== null && (
        <p className="auth__alert" role="alert">
          {error}
        </p>
      )}

      {rows !== null && rows.length === 0 && (
        <div className="la-card page__empty">
          <p>No events recorded yet.</p>
        </div>
      )}

      {rows !== null && rows.length > 0 && (
        <>
          <div className="la-card tableWrap">
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Action</th>
                  <th>Actor</th>
                  <th>Target</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>{formatDateTime(r.createdAt)}</td>
                    <td className="table__name">{humanize(r.action)}</td>
                    <td>{humanize(r.actorRole)}</td>
                    <td>{humanize(r.targetType)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {total > PAGE_SIZE && (
            <div className="toolbar" style={{ marginTop: 24 }}>
              <button
                type="button"
                className="la-btn-secondary"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Previous
              </button>
              <span className="pill">
                {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
              </span>
              <button
                type="button"
                className="la-btn-secondary"
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
